import React, { useState, useEffect } from "react";
import {
  Dialog,
  ErrorMessage,
  PrimaryButton,
  RadioButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import { Stepper, Step, StepLabel } from "@mui/material";
import {
  useFetchPartPaymentDetails,
  useCreatePaymentLink,
  useCreatePartPaymentSchedule,
} from "store/backend/assets";
import {
  INDIAN_CURRENCY_ID,
  PART_PAYMENT_FULLY_PAID_STATUS_ID,
  PART_PAYMENT_PARTIALLY_PAID_STATUS_ID,
  PAYMENT_MODE,
  PAYMENT_MODE_OPTIONS,
} from "constants/config";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-fox-toast";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmSchedule from "components/ConfirmSchedule/ConfirmSchedule";
import Loader from "Shared/Loader/Loader";
import CashAndChequePayment from "components/OfflinePayments/OfflinePayments";

import styles from "./PartPayment.module.scss";

const handleOnClickCreatePaymentLink = (
  selectedOrderId,
  stepIndex,
  amount,
  amountPaid,
  createPaymentLink,
  setPaymentLinks,
  setIsLinkCopied,
  isPartAmountPartiallyPaid
) => {
  const paymentAmount = isPartAmountPartiallyPaid
    ? amount - amountPaid
    : amount;

  const payload = {
    order_id: selectedOrderId,
    payment_link_amount: paymentAmount,
    currency_id: INDIAN_CURRENCY_ID,
    payment_type_id: 2,
    payment_flow_type_id: 2,
  };

  createPaymentLink(payload, {
    onSuccess: (paymentLink) => {
      setPaymentLinks((prev) => ({ ...prev, [stepIndex]: paymentLink }));
      setIsLinkCopied((prev) => ({ ...prev, [stepIndex]: false }));
    },
  });
};

const handleCopyLink = (stepIndex, paymentLinks, setIsLinkCopied) => {
  navigator.clipboard.writeText(paymentLinks[stepIndex]);
  setIsLinkCopied((prev) => ({ ...prev, [stepIndex]: true }));
  toast.success("Copied Payment Link!");
};

const handlePartChange = (
  event,
  setSelectedOption,
  setActiveStep,
  setPaymentLinks,
  setIsLinkCopied
) => {
  setSelectedOption(event.target.value);
  setActiveStep(0);
  setPaymentLinks({});
  setIsLinkCopied({});
};

const handleClickCreateCustomAmountPaymentLink = (
  selectedOrderId,
  customAmount,
  createPaymentLink,
  setCustomPaymentLink,
  setIsCustomLinkCopied
) => {
  const payload = {
    order_id: selectedOrderId,
    payment_link_amount: customAmount,
    currency_id: INDIAN_CURRENCY_ID,
    payment_type_id: 2,
    payment_flow_type_id: 2,
  };

  createPaymentLink(payload, {
    onSuccess: (paymentLink) => {
      setCustomPaymentLink(paymentLink);
      setIsCustomLinkCopied(false);
    },
  });
};

const handleCopyCustomLink = (customPaymentLink, setIsCustomLinkCopied) => {
  navigator.clipboard.writeText(customPaymentLink);
  setIsCustomLinkCopied(true);
  toast.success("Copied Custom Amount Payment Link!");
};

const handleCloseConfirmDialog = (setOpenDialogContent) => {
  setOpenDialogContent(false);
};

const handleConfirmAndProceed = (
  partPaymentDetails,
  selectedOption,
  selectedOrderId,
  createPartPaymentSchedule,
  setOpenDialogContent
) => {
  const selectedPartDetails = partPaymentDetails.find(
    (part) => part.no_of_parts.toString() === selectedOption
  );

  if (!selectedPartDetails) {
    toast.error("Selected payment option is not available.");
    return;
  }
  createPartPaymentSchedule(
    {
      order_id: selectedOrderId,
      subscription_part_payments: [
        {
          sku_subscription_id: selectedPartDetails.sku_subscription_id,
          payment_plan_id: selectedPartDetails.id,
          installments: selectedPartDetails.schedules.map(
            (schedule, index) => ({
              installment_number: index + 1,
              amount: schedule.amount,
              due_date: schedule.due_date,
              consist_books: schedule.consists_books,
            })
          ),
        },
      ],
    },
    {
      onSuccess: () => {
        toast.success("Part Payment Schedule Confirmed!");
        handleCloseConfirmDialog(setOpenDialogContent);
        window.location.reload();
      },
      onError: (error) =>
        toast.error(error.message || "Failed to confirm part payment schedule"),
    }
  );
};

const getInstallments = (
  isChangeSchedule,
  isPartPaymentScheduleConfirmed,
  currentPartSchedule,
  selectedPartPaymentDetails
) => {
  if (isChangeSchedule) return currentPartSchedule;
  if (isPartPaymentScheduleConfirmed) {
    return [...(selectedPartPaymentDetails[0]?.installments || [])].sort(
      (a, b) => a.installment_no - b.installment_no
    );
  }
  return currentPartSchedule;
};

const PartPayment = (props) => {
  const {
    selectedOrderId,
    isPartPaymentScheduleConfirmed,
    selectedPartPaymentDetails,
    isAnyAmountPaid,
    salesPersonId,
  } = props;
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentLinks, setPaymentLinks] = useState({});
  const [isLinkCopied, setIsLinkCopied] = useState({});
  const [customAmount, setCustomAmount] = useState("");
  const [customPaymentLink, setCustomPaymentLink] = useState(null);
  const [isCustomLinkCopied, setIsCustomLinkCopied] = useState(false);
  const [openDialogContent, setOpenDialogContent] = useState(false);
  const [isChangeSchedule, setIsChangeSchedule] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODE_OPTIONS[0].value);

  const { data: partPaymentDetails, isPending } =
    useFetchPartPaymentDetails(selectedOrderId);

  const {
    mutate: createPaymentLink,
    isError: isErrorOnCreatePaymentLink,
    error: createPartPaymentLinkErrorMessage,
  } = useCreatePaymentLink();

  const {
    mutate: createPartPaymentSchedule,
    isPending: isPartPaymentSchedulePending,
  } = useCreatePartPaymentSchedule();

  const partOptions = partPaymentDetails
    ? partPaymentDetails
        .filter(
          (item) =>
            item?.no_of_parts !== undefined && item?.no_of_parts !== null
        )
        .map((item) => ({
          label: `${item.no_of_parts} parts`,
          value: item.no_of_parts.toString(),
        }))
    : [];

  useEffect(() => {
    if (partOptions.length > 0 && !selectedOption) {
      setSelectedOption(partOptions[0].value);
    }
  }, [partOptions, selectedOption]);

  const selectedPartData = partPaymentDetails?.find(
    (item) => item?.no_of_parts?.toString?.() === selectedOption
  );

  const currentPartSchedule = selectedPartData?.schedules ?? [];

  const installmentDetails = getInstallments(
    isChangeSchedule,
    isPartPaymentScheduleConfirmed,
    currentPartSchedule,
    selectedPartPaymentDetails
  );

  const isPartAmountFullyPaid = (installmentDetail) =>
    installmentDetail?.status?.id === PART_PAYMENT_FULLY_PAID_STATUS_ID;

  const isPartAmountPartiallyPaid = (installmentDetail) =>
    installmentDetail?.status?.id === PART_PAYMENT_PARTIALLY_PAID_STATUS_ID;

  const showCreatePaymentLink = (
    installmentDetail,
    installmentIndex,
    installmentDetails
  ) => {
    const firstNotFullyPaidIndex = installmentDetails.findIndex(
      (inst) => !isPartAmountFullyPaid(inst)
    );
    return (
      isPartPaymentScheduleConfirmed &&
      !isChangeSchedule &&
      installmentIndex === firstNotFullyPaidIndex &&
      !isPartAmountFullyPaid(installmentDetail)
    );
  };

  useEffect(() => {
    if (installmentDetails.length > 0) {
      const currentIndex = installmentDetails.findIndex(
        (inst) => !isPartAmountFullyPaid(inst)
      );
      setActiveStep(
        currentIndex === -1 ? installmentDetails.length - 1 : currentIndex
      );
    }
  }, [installmentDetails]);

  return (
    <div className={styles.mainContainer}>
      {isPending ? (
        <Loader />
      ) : !selectedPartPaymentDetails && partOptions.length === 0 ? (
        <>
          <ErrorMessage>No Part Payment Details Available</ErrorMessage>
        </>
      ) : (
        <>
          <div className={styles.radioButtonContainer}>
            {!isPartPaymentScheduleConfirmed || isChangeSchedule ? (
              <>
                <Text>Select part</Text>
                <RadioButton
                  value={selectedOption}
                  options={partOptions}
                  onChange={(event) =>
                    handlePartChange(
                      event,
                      setSelectedOption,
                      setActiveStep,
                      setPaymentLinks,
                      setIsLinkCopied
                    )
                  }
                  row
                />
              </>
            ) : (
              <div className={styles.scheduleContainer}>
                <Text>
                  Selected Schedule:{" "}
                  {selectedPartPaymentDetails?.[0]?.installments?.length ||
                    "N/A"}{" "}
                  parts
                </Text>
                {!isAnyAmountPaid && (
                  <div
                    className={styles.changeSchedule}
                    onClick={() => setIsChangeSchedule(true)}
                  >
                    Change schedule
                    <EditIcon />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.partPaymentContainer}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {installmentDetails.map((installmentDetail, installmentIndex) => (
                <Step
                  className={styles.partPaymentDetails}
                  key={installmentDetail.installment_no}
                  completed={isPartAmountFullyPaid(installmentDetail)}
                >
                  <StepLabel>
                    ₹{installmentDetail.amount} - Due on{" "}
                    {installmentDetail.due_date}
                    {isPartPaymentScheduleConfirmed &&
                      isPartAmountFullyPaid(installmentDetail) && (
                        <span className={styles.fullyPaidLabel}>
                          {" "}
                          Amount Paid
                        </span>
                      )}
                  </StepLabel>
                  {showCreatePaymentLink(
                    installmentDetail,
                    installmentIndex,
                    installmentDetails
                  ) && (
                    <PrimaryButton
                      onClick={() =>
                        handleOnClickCreatePaymentLink(
                          selectedOrderId,
                          installmentIndex,
                          installmentDetail.amount,
                          installmentDetail.amount_paid,
                          createPaymentLink,
                          setPaymentLinks,
                          setIsLinkCopied,
                          isPartAmountPartiallyPaid(installmentDetail)
                        )
                      }
                    >
                      Create Payment Link for{" "}
                      {isPartAmountPartiallyPaid(installmentDetail)
                        ? installmentDetail.amount -
                          installmentDetail.amount_paid
                        : installmentDetail.amount}
                    </PrimaryButton>
                  )}
                  {paymentLinks[installmentIndex] &&
                    !isLinkCopied[installmentIndex] && (
                      <div className={styles.paymentLinkContainer}>
                        <div
                          className={styles.copyLinkContainer}
                          onClick={() =>
                            handleCopyLink(
                              installmentIndex,
                              paymentLinks,
                              setIsLinkCopied
                            )
                          }
                        >
                          <div className={styles.copyLinkText}>Copy Link</div>
                          <ContentCopyIcon className={styles.copyIcon} />
                        </div>
                      </div>
                    )}
                </Step>
              ))}
            </Stepper>
          </div>
          {(!isPartPaymentScheduleConfirmed || isChangeSchedule) && (
            <PrimaryButton onClick={() => setOpenDialogContent(true)}>
              Confirm Schedule
            </PrimaryButton>
          )}
          <div className={styles.dashedLine} />
          {isPartPaymentScheduleConfirmed && !isChangeSchedule && (
            <div className={styles.customAmount}>
              <div className={styles.field}>
                <Text>Collect Payment For Custom Amount</Text>
                <Textfield
                  label="Enter Amount"
                  value={customAmount}
                  handleOnChange={(event) => {
                    const value = event.target.value;
                    const validAmount = value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*?)\..*/g, "$1");

                    setCustomAmount(validAmount);
                  }}
                  placeholder="Enter Amount"
                />
              </div>
              <div className={styles.field}>
                <Text>Select Payment Mode</Text>
                <SingleSelect
                  value={paymentMode}
                  menuItems={PAYMENT_MODE_OPTIONS}
                  handleChange={(event) => setPaymentMode(event.target.value)}
                  tag="Select Payment Mode"
                />
              </div>
              {paymentMode === PAYMENT_MODE.ONLINE && (
                <>
                  <PrimaryButton
                    onClick={() =>
                      handleClickCreateCustomAmountPaymentLink(
                        selectedOrderId,
                        customAmount,
                        createPaymentLink,
                        setCustomPaymentLink,
                        setIsCustomLinkCopied
                      )
                    }
                    disabled={
                      !customAmount || isNaN(customAmount) || customAmount <= 0
                    }
                  >
                    Create Payment Link
                  </PrimaryButton>

                  {customPaymentLink && !isCustomLinkCopied && (
                    <div className={styles.paymentLinkContainer}>
                      <div
                        className={styles.copyLinkContainer}
                        onClick={() =>
                          handleCopyCustomLink(
                            customPaymentLink,
                            setIsCustomLinkCopied
                          )
                        }
                      >
                        <div className={styles.copyLinkText}>Copy Link</div>
                        <ContentCopyIcon className={styles.copyIcon} />
                      </div>
                    </div>
                  )}

                  {customAmount && isErrorOnCreatePaymentLink && (
                    <ErrorMessage>
                      {createPartPaymentLinkErrorMessage.message}
                    </ErrorMessage>
                  )}
                </>
              )}
              {paymentMode !== PAYMENT_MODE.ONLINE && (
                <CashAndChequePayment
                  orderId={selectedOrderId}
                  payableAmount={customAmount}
                  createdBy={salesPersonId}
                  paymentMode={paymentMode}
                />
              )}
            </div>
          )}
        </>
      )}

      <Dialog
        open={openDialogContent}
        handleClose={handleCloseConfirmDialog}
        width="500px"
      >
        <ConfirmSchedule
          onCancel={() => handleCloseConfirmDialog(setOpenDialogContent)}
          onConfirm={() =>
            handleConfirmAndProceed(
              partPaymentDetails,
              selectedOption,
              selectedOrderId,
              createPartPaymentSchedule,
              setOpenDialogContent
            )
          }
          isPending={isPartPaymentSchedulePending}
          isError={false}
          parts={selectedOption} 
        />
      </Dialog>
    </div>
  );
};

export default PartPayment;
